import { FC } from 'react'
import cn from 'classnames'
import { CheckBoxLabel } from '@UI/CheckBox'
import InfoComp from '@pages/Admin/CRMSettings/components/InfoComp/InfoComp'
import SliderField from '@pages/Admin/CourseSettings/components/SliderField/SliderField'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import CreateLinkNumberInput from '../../../../CreateLinkNumberInput'
import spclasses from '../../../../../SetCourseClassDirectionModal/purchase.module.css'
import { CREATE_LINK_BLOCK_TARIFF_TYPES } from '../../../../tariffTypes'
import { withCreateLinkBlockTariffTypesHint } from '../../../../createLinkBlockInfoContent'
import AdditionalCourseTeacherSelect from './AdditionalCourseTeacherSelect'
import GroupPerLessonAdditionalCourseFields from './GroupPerLessonAdditionalCourseFields'
import { useLessonAdditionalCourseForm } from './useLessonAdditionalCourseForm'
import { ICourseEnrollmentItem } from './types'

type LessonAdditionalCourseFieldsProps = {
  order: ICourseEnrollmentItem
  tariff?: ICourseTariff | null
  defaultDiscount?: number | null
  onChange: (
    key: keyof ICourseEnrollmentItem,
    value: number | string | boolean | null | object,
  ) => void
}

/**
 * UI доп. курса для hour / minute / group_per_lesson — паритет с основным.
 */
const LessonAdditionalCourseFields: FC<LessonAdditionalCourseFieldsProps> = ({
  order,
  tariff,
  defaultDiscount = null,
  onChange,
}) => {
  const lessonForm = useLessonAdditionalCourseForm({
    order,
    selectedTariff: tariff,
    defaultDiscount,
    onChange,
  })

  if (lessonForm.isGroupPerLesson) {
    return (
      <GroupPerLessonAdditionalCourseFields
        order={order}
        tariff={tariff}
        defaultDiscount={defaultDiscount}
        onChange={onChange}
      />
    )
  }

  return (
    <>
      <div className={spclasses.inputWithLabel}>
        <CreateLinkNumberInput
          className={spclasses.input}
          title="Длительность занятия (мин.)"
          integer
          min={1}
          max={1440}
          placeholder={60}
          value={order.lesson_duration}
          onChange={lessonForm.onChangeDuration}
        />
      </div>

      <div className={spclasses.inputWithLabel}>
        <CreateLinkNumberInput
          className={spclasses.input}
          title="Цена одного занятия (₽)"
          min={0}
          placeholder={0}
          value={order.first_price}
          onChange={lessonForm.onChangeFirstPrice}
        />
      </div>

      {lessonForm.isIndividual ? (
        <AdditionalCourseTeacherSelect
          courseId={order.courseId}
          teacherId={order.teacher_id}
          salaryPerHour={order.salary_per_hour}
          onChangeTeacher={lessonForm.onChangeTeacher}
        />
      ) : null}

      <div className={spclasses.inputWithLabel}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CheckBoxLabel
            type="button"
            value={lessonForm.isValueFixed}
            onClick={lessonForm.onToggleValueFixed}
            label="Зафиксировать кол-во занятий для оплаты"
            className={spclasses.checkboxWithLabel}
            style={{
              justifyContent: 'flex-start',
              paddingTop: '4px',
              paddingBottom: '4px',
              height: 'auto',
            }}
          />
          <InfoComp
            text={withCreateLinkBlockTariffTypesHint(
              'По умолчанию клиент при покупке сам выбирает количество занятий. При включении чекбокса количество фиксируется.',
              CREATE_LINK_BLOCK_TARIFF_TYPES.lessonCount,
            )}
          />
        </div>
        <div style={{ paddingLeft: '8px', paddingTop: '4px' }}>
          <SliderField
            label="Количество занятий"
            value={lessonForm.lessonCount}
            extraContent={lessonForm.lessonCount.toString()}
            min={1}
            max={100}
            onChange={lessonForm.onChangeLessonCount}
          />
        </div>
      </div>

      <div className={spclasses.inputWithLabel}>
        {lessonForm.isPriceEditable ? (
          <CreateLinkNumberInput
            className={spclasses.input}
            title="Итого к оплате (₽)"
            min={0}
            value={order.price ?? lessonForm.suggestedPrice}
            onChange={(value) => {
              lessonForm.onChangePrice(value as number)
            }}
          />
        ) : (
          <TooltipWrapper
            placement="top"
            title="Итого считается автоматически (цена занятия × количество). Чтобы задать сумму вручную, зафиксируйте количество занятий."
          >
            <span style={{ display: 'block', cursor: 'not-allowed' }}>
              <CreateLinkNumberInput
                className={cn(spclasses.input, spclasses.disabledInput)}
                title="Итого к оплате (₽)"
                min={0}
                value={lessonForm.suggestedPrice}
                disabled
              />
            </span>
          </TooltipWrapper>
        )}
        <div className={spclasses.tariffTypeHint} style={{ marginTop: 4 }}>
          Расчётная сумма: {lessonForm.suggestedPrice} ₽ (
          {order.first_price ?? 0} ₽ × {lessonForm.lessonCount})
        </div>
      </div>
    </>
  )
}

export default LessonAdditionalCourseFields
